import React from 'react';
import { faCircleNodes } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/context-graphs-clarity.jpg
import contextGraphsClarityImg from '../../../assets/images/blogs/context-graphs-clarity.jpg';

const ContextGraphsClarity = () => (
    <BlogPost
        title="Context Graphs: Agents Making and Learning from THEIR Decisions"
        subtitle="Decision traces are captured in the execution path, not defined upfront in workshops, not reconstructed after the fact via ETL."
        category="Architecture"
        categoryIcon={faCircleNodes}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={contextGraphsClarityImg}
        imageAlt="Context graphs - runtime artefacts for agentic decision-making"
        ctaHeading="Ready for agentic decision traces?"
        ctaText="Book a discovery call and we'll show you how AgentDOG captures institutional memory through execution."
        seoDescription="Clarifying what context graphs actually are: runtime artefacts for agentic decision-making, distinct from traditional knowledge management approaches."
        seoKeywords="context graphs, agentic AI, decision traces, institutional memory, AgentDOG, Nebulyx"
        slug="context-graphs-clarity"
    >
        <p>
            Context graphs are about agents making and learning from their decisions.
        </p>

        <p>
            There seems to be a lot of confusion as other domains and problem spaces are
            being conflated with this concept.
        </p>

        <blockquote className="blog-post-quote">
            "Decision traces are captured in the execution path, not defined upfront in
            workshops, not reconstructed after the fact via ETL. They emerge as a byproduct
            of agents doing work."
        </blockquote>

        <h3>This Is About Agentic Decision-Making</h3>

        <p>
            This discussion is about agentic decision-making, not human decision-making.
            The perspective is clear: "AI agents are hitting a wall. The wall isn't missing
            data. It's missing decision traces."
        </p>

        <p>
            Context graphs serve as the runtime artefact for agents, with the "context"
            term coming from the typical information passed into each LLM inference/query
            during execution - which the AI world calls "context".
        </p>

        <p>
            Worth noting: context graphs contain more than just information passed - i.e.
            all the foundational information the Agent uses to make its decision.
        </p>

        <p>
            They provide a decision log/trace generated through purely agentic execution,
            rather than extracting data from humans post-factum.
        </p>

        <p>
            The same agents and other agents can then learn from these specific agentic
            decisions.
        </p>

        <h3>What Context Graphs Are NOT</h3>

        <p>
            Context graphs are distinct from:
        </p>

        <p>
            <strong>1.</strong> How people make decisions, including capturing those decisions.
        </p>

        <p>
            <strong>2.</strong> Determining when to use agents versus people or other methods.
        </p>

        <p>
            <strong>3.</strong> Deciding whether to model workflows upfront or adopt agentic
            approaches (context graphs fall into the latter).
        </p>

        <p>
            <strong>4.</strong> General knowledge management.
        </p>

        <p>
            <strong>5.</strong> General metadata approaches.
        </p>

        <p>
            <strong>6.</strong> Typical Knowledge graphs - which typically try to capture
            meaning upfront.
        </p>

        <p>
            <strong>7.</strong> The best graph modelling approaches (e.g. RDF etc).
        </p>

        <p>
            While these are all important questions, and definitely part of a bigger
            question of how AI and Agents are changing these, it's crucial to maintain
            clarity and not blur the boundaries of the problem space.
        </p>

        <h3>How We Use Graphs</h3>

        <p>
            With our AgentDOG framework, we use graphs extensively - for the core execution
            and for learning - and we persist them automatically as Data Object Graphs.
        </p>

        <p>
            I.e. use them for runtime execution and persistence → the "institutional memory".
        </p>

        <h3>A Holistic View Is Required</h3>

        <p>
            I know there are a lot of questions around this specific topic, and indeed the
            others I've listed. We've been building agents at Nebulyx AI, using graph-based
            approaches for execution, and navigating these exact decision points with clients.
        </p>

        <p>
            If your org needs clarity on your agent strategy, decisioning approach, and
            knowledge management - these can't be tackled in isolation.
        </p>

        <p>
            These decisions need to be holistic and consider agentic design/execution/
            appropriateness in conjunction with the other topics.
        </p>

        <p>
            Which requires a broad perspective. 🎯
        </p>
    </BlogPost>
);

export default ContextGraphsClarity;
